import { ForecastTableContentViewModel } from '@/corePagesModule/BaseTableContent/baseTableContentViewModel';
import {
  ForecastAccuracyHeaderKey,
  isCustomerAnnounceForAccuracyKey,
} from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { TableContentItem } from '@/corePagesModule/BaseTableContent/interface/tableContentItems';
import { IYearMonth } from '@/coreDomainsModule/date';
import { UserAccountService } from '@/coreDomainsModule/services/userAccountService';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { UserAccount } from '@/coreDomainsModule/entities/users';

/**
 * 内示正答率のテーブル表示に使うロジック
 */
export class ForecastAccuracyTableContentViewModel extends ForecastTableContentViewModel {
  /**
   * 内示正答率の項目表示用
   * @param headerKey - ヘッダーキー
   * @param item - テーブルコンテンツアイテム
   * @param forecastStartYearMonth - 内示の開始年月
   */
  public displayForecastAccuracyItem = (
    headerKey: ForecastAccuracyHeaderKey,
    item: TableContentItem,
    forecastStartYearMonth?: IYearMonth
  ): string => {
    switch (headerKey) {
      case 'ForecastAccuracyName':
        return item.forecastForReceiptName ?? '';
      case 'ForecastAccuracyResponsibleUser':
        return (
          item.forecastForReceiptResponsibleUsers
            ?.map((user: UserAccount) => UserAccountService.getDisplayName(UserAccountFactory.create(user)))
            .join(', ') ?? '--'
        );
      case 'ForecastAccuracyPreviousMonthSalesResult':
        return item.previousMonthSalesResult !== undefined && item.previousMonthSalesResult !== null
          ? item.previousMonthSalesResult.addComma()
          : '--';
      case 'ForecastAccuracyPreviousMonthForecast':
        return item.previousMonthForecast !== undefined && item.previousMonthForecast !== null
          ? item.previousMonthForecast.addComma()
          : '--';
      case 'ForecastAccuracyPreviousMonthAccuracyRate':
        return item.previousMonthAccuracyRate !== undefined && item.previousMonthAccuracyRate !== null
          ? `${item.previousMonthAccuracyRate}%`
          : '--';
      default:
        if (isCustomerAnnounceForAccuracyKey(headerKey)) {
          return this._displayForecastItem(this._getForecastIndex(headerKey), item, forecastStartYearMonth);
        }
        return '';
    }
  };

  /**
   * ヘッダーのテキストを取得する
   * @param headerKey - ヘッダーキー
   * @param forecastStartYearMonth - 内示の開始年月
   */
  public getHeaderText = (headerKey: ForecastAccuracyHeaderKey, forecastStartYearMonth?: IYearMonth): string => {
    if (isCustomerAnnounceForAccuracyKey(headerKey)) {
      return this._forecastHeader(this._getForecastIndex(headerKey), forecastStartYearMonth);
    }
    if (headerKey === 'ForecastAccuracyMenu' || headerKey === 'Space8px') {
      return '';
    }
    return headerKey;
  };

  /**
   * 内示列の表示内容
   * @param index - 内示のインデックス
   * @param item - テーブルの表示内容
   * @param forecastStartYearMonth - 内示の開始年月
   */
  _displayForecastItem = (index: number, item: TableContentItem, forecastStartYearMonth?: IYearMonth): string => {
    const customerForecasts = item.customerForecasts;
    if (!customerForecasts) return '';

    const date = this.getIYearMonth(
      forecastStartYearMonth ? new Date(forecastStartYearMonth.year, forecastStartYearMonth.month - 1) : new Date(),
      index
    );
    const yearMonthInfo = { year: date.year, month: date.month };
    const forecastIndex = customerForecasts.findIndex(
      (item) => item.targetYear === yearMonthInfo.year && item.targetMonth === yearMonthInfo.month
    );
    if (forecastIndex === -1) return '-';

    const amount = customerForecasts[forecastIndex].amount;
    // Handle null case (no forecast) - display as "-"
    if (amount === null || amount === undefined) return '-';

    return `${amount.addComma()}%`;
  };
}
